'use client'

import { Box, Stack, Typography } from '@mui/material'
import { Button } from '@ui'
import { useAppOpenModal } from 'app/_hooks/useAppOpenModal'
import { Route } from 'next'
import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { Trans, useTranslation } from 'react-i18next'
import browserEnv from '~/libs/env/browserEnv'
import { sendLog } from '~/libs/ga'
import { Colors } from '~/libs/mui/color'
import { AppOpenButton } from './AppOpenButton'

const BOTTOM_NAV_HEIGHT = 56

const ADJUST_URL =
  'https://samansa.go.link/samansa.com?adj_t=20bsz5s2&adj_campaign=fixedInstallPrompt_fixed'
const ADJUST_BLANK_URL = 'https://samansa.go.link/samansa.com?adj_t='

export const AppOpenModal = () => {
  const { hidden } = useAppOpenModal()
  const [showFooter, setShowFooter] = useState(false)
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { t } = useTranslation()

  const [hasBottomNav, setHasBottomNav] = useState(false)

  useEffect(() => {
    const check = () => setHasBottomNav(!!document.querySelector('.MuiBottomNavigation-root'))
    check()
    const obs = new MutationObserver(check)
    obs.observe(document.body, { childList: true, subtree: true })
    return () => obs.disconnect()
  }, [pathname])

  const appLink = useMemo(() => {
    const deepLinkUrl = `${browserEnv.NEXT_PUBLIC_APP_LINK}${pathname}${
      searchParams && searchParams.toString() ? `?${searchParams}` : ''
    }`

    const adjT = searchParams.get('adj_t')
    if (adjT) {
      return ADJUST_BLANK_URL + adjT + `&deep_link=${encodeURIComponent(deepLinkUrl)}`
    }

    return ADJUST_URL + `&deep_link=${encodeURIComponent(deepLinkUrl)}`
  }, [pathname, searchParams])

  const footerLogSent = useRef(false)

  useEffect(() => {
    if (pathname.startsWith('/webview/')) return
    if (pathname.startsWith('/survey_chats/')) return
    if (pathname === '/club') return
    if (pathname.startsWith('/login')) {
      setShowFooter(false)
      return
    }

    if (isMobile) {
      if (!footerLogSent.current) {
        sendLog({
          screen: 'app_open_modal_v2',
          action: 'view',
        })
        footerLogSent.current = true
      }
      setShowFooter(true)
    }
  }, [pathname])

  const footerRef = useRef<HTMLDivElement>(null)
  const [footerHeight, setFooterHeight] = useState(showFooter ? 113 : 0)

  useEffect(() => {
    if (!footerRef.current) return
    const obs = new ResizeObserver(([e]) => setFooterHeight(e.contentRect.height))
    obs.observe(footerRef.current)
    return () => obs.disconnect()
  }, [showFooter])

  if (showFooter) {
    return (
      <>
        <Box sx={{ height: footerHeight }} />
        <Box
          ref={footerRef}
          className='app-open-modal-footer'
          sx={{
            position: 'fixed',
            bottom: hasBottomNav ? BOTTOM_NAV_HEIGHT : 0,
            left: 0,
            width: '100%',
            zIndex: 1199,
            transform: hidden ? 'translateY(100%)' : 'none',
            transition: hidden
              ? 'transform 0.15s cubic-bezier(0, 0.5, 0.7, 0.7)'
              : 'transform 0.15s cubic-bezier(0.3, 0, 0.3, 1)',
          }}
        >
          <Box sx={{ height: '3px', backgroundColor: Colors.yellow }} />
          <Stack
            spacing={1.5}
            sx={{
              backgroundColor: '#212121',
              padding: '16px',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Typography variant='body1' fontWeight={700}>
              <Trans i18nKey='Enjoy a smoother experience with the SAMANSA app' />
            </Typography>
            <Stack direction='row' spacing={1} sx={{ width: '100%' }}>
              <AppOpenButton />
              <Link href={appLink as Route} passHref style={{ textDecoration: 'none', flex: 1 }}>
                <Button
                  variant='outlined'
                  fullWidth
                  onClick={() => {
                    sendLog({
                      screen: 'app_open_modal_v2',
                      action: 'download_app',
                    })
                  }}
                >
                  {t('Download the app')}
                </Button>
              </Link>
            </Stack>
          </Stack>
        </Box>
      </>
    )
  }

  return null
}
